import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface TextFragment {
  text: (string | { tag: string; href?: string; text?: string })[];
}

@Component({
  selector: 'app-about',
  imports: [],
  templateUrl: './about.component.html',
  styleUrl: './about.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AboutComponent {
  public el = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);
  private cdRef = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  readonly TYPING_SPEED_MS = 25;

  constructedText: string = '';
  constructedHref: string | undefined = '';
  finalHtml: string = '';

  currentFragmentIndex: number = 0;
  currentCharIndex: number = 0;

  typingInProgress: boolean = false;
  typeFinished: boolean = false;

  isShow: boolean = false;

  language: string = 'es';

  fullTextFragments: TextFragment = {
    text: [
      $localize`Hola, soy Francisco Peña, `,
      { tag: 'strong', text: $localize`:@@role:Senior Software Engineer` },
      $localize`.`,
      { tag: 'br' },
      { tag: 'br' },

      $localize`Tengo más de 13 años de experiencia desarrollando `,
      {
        tag: 'strong',
        text: $localize`:@@domain:software empresarial y aplicaciones web`,
      },
      $localize`. Mi especialidad actual es `,
      { tag: 'strong', text: $localize`:@@stack:Java / Spring Boot` },
      $localize`, con experiencia full-stack en `,
      { tag: 'strong', text: $localize`:@@frontend:Angular` },
      $localize` cuando el proyecto lo requiere.`,
      { tag: 'br' },
      { tag: 'br' },

      $localize`Me gusta programar, mejorar sistemas existentes y construir software `,
      {
        tag: 'strong',
        text: $localize`:@@value1:robusto, mantenible y fiable en producción`,
      },
      $localize`. He trabajado tanto en proyectos desde cero como en la evolución de sistemas críticos, adaptándome a distintos equipos y contextos técnicos.`,
      { tag: 'br' },
      { tag: 'br' },

      $localize`Me siento cómodo en todo el ciclo de desarrollo: análisis, implementación, `,
      { tag: 'strong', text: $localize`:@@quality:testing automatizado` },
      $localize` y despliegue. Suelo trabajar con `,
      { tag: 'strong', text: $localize`:@@tools:Git, Docker y CI/CD` },
      $localize` para asegurar entregas consistentes.`,
      { tag: 'br' },
      { tag: 'br' },

      $localize`Si buscas un perfil técnico senior orientado a desarrollo real y a que las cosas funcionen bien en producción, `,
      {
        tag: 'a',
        href: '#contact',
        text: $localize`:@@contactLink:estaré encantado de hablar contigo.`,
      },
    ],
  };

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const url = window.location.href;
      const urlParts = url.split('/');
      const lan = urlParts[3];
      this.language = lan === 'en' ? 'en' : 'es';
    }
  }

  @HostListener('window:scroll')
  onScroll() {
    const componentPosition = this.el.nativeElement.offsetTop;
    const scrollPosition = window.pageYOffset;

    if (scrollPosition >= componentPosition - 850) {
      if (!this.typingInProgress && !this.typeFinished) {
        this.typeText();
      }
    }
  }

  typeText() {
    if (this.currentFragmentIndex >= this.fullTextFragments.text.length) {
      return;
    }
    this.typingInProgress = true;
    const currentFragment =
      this.fullTextFragments.text[this.currentFragmentIndex];

    if (typeof currentFragment === 'string') {
      if (this.currentCharIndex < currentFragment.length) {
        this.constructedText += currentFragment[this.currentCharIndex];
        this.currentCharIndex++;
        setTimeout(() => this.typeText(), this.TYPING_SPEED_MS);
      } else {
        this.finalHtml += this.constructedText;
        this.constructedText = '';
        this.currentCharIndex = 0;
        this.currentFragmentIndex++;
        this.typeText();
      }
    } else if (currentFragment.tag === 'strong') {
      if (
        currentFragment.text &&
        this.currentCharIndex < currentFragment.text.length
      ) {
        this.constructedText += currentFragment.text[this.currentCharIndex];
        this.currentCharIndex++;
        setTimeout(() => this.typeText(), this.TYPING_SPEED_MS);
      } else {
        this.finalHtml += `<strong>${this.constructedText}</strong>`;
        this.constructedText = '';
        this.currentCharIndex = 0;
        this.currentFragmentIndex++;
        this.typeText();
      }
    } else if (currentFragment.tag === 'a') {
      if (
        currentFragment.text &&
        this.currentCharIndex < currentFragment.text.length
      ) {
        this.constructedText += currentFragment.text[this.currentCharIndex];
        this.constructedHref = currentFragment.href;
        this.currentCharIndex++;
        setTimeout(() => this.typeText(), this.TYPING_SPEED_MS);
      } else {
        this.finalHtml += `<strong><a href="${this.constructedHref}">${this.constructedText}</a></strong>`;
        this.constructedText = '';
        this.constructedHref = '';
        this.currentCharIndex = 0;
        this.currentFragmentIndex++;
        this.typeText();
      }
    } else if (currentFragment.tag === 'br') {
      this.finalHtml += '<br>';
      this.currentFragmentIndex++;
      setTimeout(() => this.typeText(), this.TYPING_SPEED_MS);
    }

    if (this.currentFragmentIndex >= this.fullTextFragments.text.length) {
      this.typingInProgress = false;
      this.typeFinished = true;
    }
    this.cdRef.detectChanges();
  }

  getSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.finalHtml + this.constructedText,
    );
  }
}
